import { Component, ViewChild, OnInit, inject, CUSTOM_ELEMENTS_SCHEMA, ChangeDetectionStrategy, ChangeDetectorRef, DestroyRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormBuilder, ReactiveFormsModule, UntypedFormGroup } from '@angular/forms';
import { IonicModule } from '@ionic/angular';
import { MatMenuModule } from '@angular/material/menu';
import { debounceTime } from 'rxjs/operators';
import { Subscription } from 'rxjs';

import { CustomNavController } from 'src/shared/providers/navigation/custom-nav-controller';
import { RightSidePanelService } from 'src/shared/providers/providers/right-side-panel';
import { TidyStorage } from 'src/shared/providers/tidy-storage';
import { WindowService } from 'src/shared/providers/window.service';
import { Util } from 'src/shared/util/util';

import { PaymentDetailPage } from 'src/pages/payments/payment-detail/payment-detail';
import { RequestPaymentPage } from 'src/pages/payments/request-payment/request-payment';
import { RentCollectionSettingsPage } from 'src/pages/payments/rent-collection-settings/rent-collection-settings';
import { LeaseLedgerPage } from 'src/pages/leases/lease-ledger/lease-ledger';

import {
  RentCollectionService,
  Payment,
  PaymentStatus,
} from 'src/shared/services/rent-collection.service';
import { propertyLabel as sharedPropertyLabel } from 'src/shared/util/rent-label';

import { ParseDateTimePipe } from 'src/shared/pipes/parse-date.pipe';

import { TidyUiComponentsModule } from 'src/tidy-ui-components/tidy-ui-components.module';
import { SharedModule } from 'src/shared/shared.module';

// Precomputed mobile-card view model — templates never call functions (CLAUDE.md rule).
export interface PaymentRowView {
  payment: Payment;
  propertyLabel: string;
  leaseLabel: string; // "Lease · {property}" shown only when the payment links to a lease
  statusColor: string;
}

export interface StatusFilterOption {
  status: PaymentStatus;
  count: number;
  color: string;
}

@Component({
  selector: 'payments-content',
  templateUrl: './payments-content.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    IonicModule,
    MatMenuModule,
    TidyUiComponentsModule,
    SharedModule,
  ],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class PaymentsContentComponent implements OnInit {
  private cdr = inject(ChangeDetectorRef);
  private destroyRef = inject(DestroyRef);
  private rentService = inject(RentCollectionService);
  private navCtrl = inject(CustomNavController);
  private rightSidePanelService = inject(RightSidePanelService);
  private storage = inject(TidyStorage);
  private fb = inject(FormBuilder);
  private util = inject(Util);

  readonly windowService = inject(WindowService);

  loaded = false;
  errorMessage: string;
  private panelClosedSubscription: Subscription;

  searchForm: UntypedFormGroup;
  payments: Payment[] = [];
  headers: string[] = ['Payer', 'For', 'Property', 'Amount', 'Due', 'Status', 'last-column'];
  rows: any[] = [];
  shownPaymentViews: PaymentRowView[] = [];
  emptyStateMessage = '';

  // Single-select status filter — no stacking, no "All" option; the active
  // chip (or re-picking the same status) clears it.
  selectedStatus: PaymentStatus | null = null;
  activeFilter: StatusFilterOption | null = null;
  filterOptions: StatusFilterOption[] = [];

  requestPaymentAction = () => this.requestPayment();

  @ViewChild('tidyTable') tidyTable: any;

  constructor() {
    this.searchForm = this.fb.group({ search: [''] });
    this.searchForm.get('search')?.valueChanges.pipe(
      debounceTime(300),
      takeUntilDestroyed(this.destroyRef)
    ).subscribe(() => this.applyFilters());
  }

  async ngOnInit() {
    try {
      await this.loadPage();
    } catch (err) {
      this.errorMessage = err?.error?.message || err?.message || 'Failed to load payments';
      this.util.showError(this.errorMessage, 10000);
    }
  }

  async loadPage() {
    this.payments = await this.rentService.getPayments();
    this.buildFilterOptions();
    this.applyFilters();
    this.loaded = true;
    this.cdr.markForCheck();
  }

  buildFilterOptions(): void {
    const statuses: PaymentStatus[] = ['Due', 'Overdue', 'Paid', 'Failed'];
    this.filterOptions = statuses.map(status => ({
      status,
      count: this.payments.filter(p => p.status === status).length,
      color: this.statusColor(status),
    }));
    this.activeFilter = this.filterOptions.find(option => option.status === this.selectedStatus) || null;
  }

  selectFilter(status: PaymentStatus): void {
    this.selectedStatus = this.selectedStatus === status ? null : status;
    this.activeFilter = this.filterOptions.find(option => option.status === this.selectedStatus) || null;
    this.applyFilters();
  }

  clearFilter(): void {
    this.selectedStatus = null;
    this.activeFilter = null;
    this.applyFilters();
  }

  applyFilters(): void {
    const search = (this.searchForm.get('search')?.value || '').toLowerCase();
    const searching = search.length >= 2;
    let filtered = this.selectedStatus
      ? this.payments.filter(p => p.status === this.selectedStatus)
      : [...this.payments];
    if (searching) {
      filtered = filtered.filter(p =>
        `${p.payer_name} ${p.description} ${this.propertyLabel(p)} ${p.status}`.toLowerCase().includes(search)
      );
    }
    this.rows = this.buildRows(filtered);
    this.shownPaymentViews = this.buildPaymentViews(filtered);
    this.emptyStateMessage = this.selectedStatus || searching
      ? 'No payments match this view.'
      : 'No payments yet. Request a payment or wait for the next recurring rent charge.';
    this.cdr.markForCheck();
  }

  buildRows(payments: Payment[]): any[] {
    // `due_date` is date-only ('2026-08-01'); `new Date()` would read it as UTC
    // midnight and render the previous day west of UTC, disagreeing with the
    // mobile card below (which uses the `date` pipe and parses it as local).
    const datePipe = new ParseDateTimePipe();
    return payments.map(p => ({
      id: p.id,
      'Payer': { value: `${p.payer_name} (${p.payer_type})` },
      'For': { value: p.description },
      'Property': { value: this.propertyLabel(p), icon: 'assets/svg/home-outline.svg' },
      'Amount': { value: `$${p.amount.toLocaleString('en-US')}` },
      'Due': { value: datePipe.transform(p.due_date, 'MMM d, yyyy') },
      'Status': { value: this.statusCellHtml(p) },
      'last-column': { value: '', hasChevron: true },
    }));
  }

  // The same colored dot the filter menu uses, inline before the status word (the table
  // renders cell values via innerHTML, so this survives sanitization). A cell `color`
  // would instead render tidy-tag — a home glyph — which belongs on Property.
  private statusCellHtml(payment: Payment): string {
    const label = payment.cadence === 'weekly' ? `${payment.status} · Weekly` : payment.status;
    const color = this.statusColor(payment.status);
    return `<span style="display:inline-block;width:8px;height:8px;border-radius:50%;background-color:${color};margin-right:6px;vertical-align:middle;"></span>${label}`;
  }

  // Precomputed mobile-card view models — templates never call functions (CLAUDE.md rule).
  // Rebuilt in applyFilters() wherever the shown (filtered) list changes.
  buildPaymentViews(payments: Payment[]): PaymentRowView[] {
    return payments.map(payment => ({
      payment,
      propertyLabel: this.propertyLabel(payment),
      leaseLabel: payment.lease_id ? `Lease · ${payment.property_name}` : '',
      statusColor: this.statusColor(payment.status),
    }));
  }

  // `unit` already equals the room label for room payments (e.g. "Room A"); only append
  // `space_name` when it adds new information, so whole-unit payments render unchanged.
  propertyLabel(payment: Payment): string {
    return sharedPropertyLabel(payment.property_name, payment.unit, payment.space_name);
  }

  statusColor(status: string): string {
    switch (status) {
      case 'Paid': return 'var(--primary-button, #4295a3)';
      case 'Due': return '#F9A825';
      case 'Overdue': return '#C62828';
      case 'Failed': return '#C62828';
      default: return '#1E2125';
    }
  }

  onRowClick(event: { column: string; element: any }): void {
    const payment = this.payments.find(p => p.id === event.element.id);
    if (payment) { this.goToPage('payment-detail', { payment }, PaymentDetailPage); }
  }

  // Deep-links from a tenant payment row to the lease it's credited against.
  // tidy-text's (action) already stops propagation on the underlying click,
  // so this never also triggers the card's onRowClick.
  goToLease(leaseId: number): void {
    if (!leaseId) { return; }
    this.goToPage('lease-ledger', { leaseId }, LeaseLedgerPage);
  }

  requestPayment(): void {
    this.goToPage('request-payment', {}, RequestPaymentPage);
  }

  goToSettings(): void {
    this.goToPage('rent-collection-settings', {}, RentCollectionSettingsPage);
  }

  // ---- Panel navigation ----
  async goToPage(url: string, params: any, component: any): Promise<void> {
    const isMobileResolution = !this.windowService.isDesktopRes;
    if (!isMobileResolution) {
      const isRightSidePanelAlreadyOpen = await this.storage.retrieve('dialog-right-side-open');
      if (isRightSidePanelAlreadyOpen) {
        this.rightSidePanelService.navigateTo(url, params, component);
      } else {
        this.storage.save('dialog-right-side-open', true);
        this.storage.save('dialog-params', params);
        this.rightSidePanelService.openRightSidePanel(component);
      }
      this.panelClosedSubscription?.unsubscribe();
      this.panelClosedSubscription = this.rightSidePanelService.panelClosed().pipe(
        takeUntilDestroyed(this.destroyRef)
      ).subscribe(async () => {
        await this.loadPage();
      });
    } else {
      this.navCtrl.navigateForward(url, params);
    }
  }
}
